import type {
  FilterGroup,
  TableQueryParams,
} from "@/v2-backoffice-client/components/table"

// Server-side filters for the historic (durable-store) Events path only. That
// path is paged, so filtering must happen server-side to span the whole window
// rather than just the loaded pages (manualFiltering=true). Live windows (<=7d)
// load the whole window and filter client-side instead, so these are not used
// there.
export type SignInFilters = {
  search?: string
  isFailure?: boolean
  location?: string
  wasBlocked?: boolean
  sources?: string[]
  excludeSearch?: string
}

// Maps the table's emitted filters onto the historic-store server filter input.
// "Search" is the virtual column the table emits for the global search box (via
// the Table searchColumn prop); it becomes a server-side name/email match. The
// App column is multi-select, so it arrives as `in`. Risk is not filtered here
// — the per-event risk column is gone from the Events tab (risk is an aggregate;
// it lives on the Users tab).
export function deriveFilters(params: TableQueryParams): SignInFilters {
  const next: SignInFilters = {}
  for (const f of params.filters ?? []) {
    if (f.column === "Search" && f.contains) next.search = f.contains
    else if (f.column === "Location" && f.contains) next.location = f.contains
    else if (f.column === "IsFailure" && f.eq != null)
      next.isFailure = f.eq === "true"
    else if (f.column === "WasBlocked" && f.eq != null)
      next.wasBlocked = f.eq === "true"
    else if (f.column === "Source" && f.in && f.in.length > 0)
      next.sources = f.in
  }
  const exclude = extractExcludeSearch(params.filterTree)
  if (exclude) next.excludeSearch = exclude
  return next
}

// Best-effort mapping of the advanced builder's "User is not …" condition onto
// the server excludeSearch (a NOT LIKE on name/email). Only top-level negated
// conditions on the User column are mapped; richer nested trees stay a
// client-side (live-window) feature.
function extractExcludeSearch(
  tree: FilterGroup | undefined,
): string | undefined {
  for (const cond of tree?.conditions ?? []) {
    if (cond.column !== "UserName") continue
    const negated = cond.negate === true || cond.operator === "NEQ"
    if (!negated) continue
    if (cond.operator === "IN" && cond.values && cond.values.length > 0) {
      return cond.values[0]
    }
    if (cond.value) return cond.value
  }
  return undefined
}
